import { Box, Divider, Stack, styled, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { CONVERSATION_COLUMN_MAX_WIDTH } from 'src/pages/Conversations/constants';

const MessageGroupHeader = styled(Box, {
  name: 'MessageGroupHeader',
})(({ theme }) => ({
  position: 'relative',
  width: '90%',
  alignSelf: 'center',
  minHeight: theme.spacing(3),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const MessageGroupDayPillContainer = styled(Box, {
  name: 'MessageGroupDayPillContainer',
})(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(0.5),
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    top: theme.spacing(0.25),
  },
  marginTop: `calc(-1 * ${theme.spacing(5.3)})`,
}));

const MessageGroupDayPill = styled(Box, { name: 'MessageGroupDayPill' })(({ theme }) => ({
  ...(theme.palette.mode === 'dark'
    ? {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
      }
    : {
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${alpha(theme.palette.common.black, 0.12)}`,
      }),
  borderRadius: 999,
  padding: `1px ${theme.spacing(1.5)}`,
  boxShadow: theme.shadows[1],
  pointerEvents: 'none',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.25, 1.25),
  },
}));

const MessageGroupDayPillText = styled(Typography, {
  name: 'MessageGroupDayPillText',
})(() => ({
  fontSize: '0.625rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  lineHeight: 2.3,
}));

const MessageGroupSeparatorLine = styled(Divider, {
  name: 'MessageGroupSeparatorLine',
})(({ theme }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  color: theme.palette.custom.OV.sys.onSurface,
  '&.MuiDivider-root::before, &.MuiDivider-root::after': {
    borderColor: theme.palette.custom.OV.sys.outlineVariant,
  },
}));

export const ThreadColumn = styled(Stack, { name: 'ThreadColumn' })(({ theme }) => ({
  width: '100%',
  maxWidth: CONVERSATION_COLUMN_MAX_WIDTH,
  marginInline: 'auto',
  paddingInline: theme.spacing(1),
  flexShrink: 0,
}));

const DaySectionContainer = styled(ThreadColumn, { name: 'DaySectionContainer' })(() => ({
  flexDirection: 'column-reverse',
}));

interface DaySectionProps {
  label: string;
  children: React.ReactNode;
  showSeparator?: boolean;
}

export const DaySection = ({ label, children, showSeparator = true }: DaySectionProps) => {
  return (
    <DaySectionContainer>
      {children}
      <MessageGroupDayPillContainer>
        <MessageGroupDayPill data-testid="chat-day-separator">
          <MessageGroupDayPillText variant="caption">{label}</MessageGroupDayPillText>
        </MessageGroupDayPill>
      </MessageGroupDayPillContainer>
      {showSeparator ? (
        <MessageGroupHeader>
          <MessageGroupSeparatorLine />
        </MessageGroupHeader>
      ) : null}
    </DaySectionContainer>
  );
};
